import dayjs from 'dayjs'
import { useEffect, useRef } from 'react'
import { Box, Typography, useTheme, Button } from '@mui/material'
import Psychology from '@mui/icons-material/Psychology'
import LocalHospital from '@mui/icons-material/LocalHospital'
import CalendarToday from '@mui/icons-material/CalendarToday'
import Person from '@mui/icons-material/Person'
import Event from '@mui/icons-material/Event'
import Healing from '@mui/icons-material/Healing'
import Assignment from '@mui/icons-material/Assignment'
import { isValidNumber, getExactDiffFromCurrentTimeDayJs } from 'src/consumer/helpers/utilFunctions'
import { styled } from '@mui/material/styles'

// ─── Layout ──────────────────────────────────────────────────────────────────

const SummaryContainer = styled(Box)(({ theme }) => ({
  display: 'grid',
  gap: theme.spacing(1.5),
  width: '100%',
  '@media (min-width: 900px)': { gridTemplateColumns: '1fr 1fr' },
  '@media (max-width: 899px)': { gridTemplateColumns: '1fr' },
}))

const SummaryCard = styled(Box)(({ theme, gradient }) => ({
  background: '#ffffff',
  borderRadius: '8px',
  padding: '10px 12px',
  border: '1px solid rgba(0, 0, 0, 0.07)',
  boxShadow: '0 1px 4px rgba(0, 0, 0, 0.06)',
  position: 'relative',
  overflow: 'hidden',
  boxSizing: 'border-box',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '2px',
    background: gradient || 'linear-gradient(90deg, #6366f1 0%, #8b5cf6 100%)',
  },
}))

// ─── Card Header ─────────────────────────────────────────────────────────────

const CardHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  marginBottom: theme.spacing(0.75),
  paddingBottom: theme.spacing(0.75),
  borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
}))

const IconWrapper = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'bgColor',
})(({ bgColor }) => ({
  width: '28px',
  height: '28px',
  borderRadius: '6px',
  flexShrink: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: bgColor || 'rgba(99, 102, 241, 0.12)',
}))

const CardTitle = styled(Typography)(() => ({
  fontSize: '13px',
  fontWeight: 600,
  color: '#374151',
  lineHeight: 1.3,
  letterSpacing: '0.1px',
}))

// ─── Info rows ───────────────────────────────────────────────────────────────

// Wrapping grid: up to 2 items per row, each item sized to its content
const InfoGrid = styled(Box)(() => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: '4px 12px',
}))

// Each chip: label + value inline, tightly packed
const InfoRow = styled(Box)(() => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '4px',
  padding: '2px 6px',
  borderRadius: '5px',
  background: 'rgba(0, 0, 0, 0.02)',
  border: '1px solid rgba(0,0,0,0.04)',
  minWidth: 0,
  flexShrink: 0,
}))

const InfoLabel = styled(Typography)(() => ({
  fontSize: '10px',
  fontWeight: 500,
  color: '#9ca3af',
  display: 'flex',
  alignItems: 'center',
  gap: '3px',
  whiteSpace: 'nowrap',
  flexShrink: 0,
  '&::after': { content: '":"' },
}))

const InfoValue = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: '3px',
  minWidth: 0,
}))

const ValueText = styled(Typography)(() => ({
  fontSize: '12px',
  fontWeight: 600,
  color: '#111827',
  lineHeight: 1.3,
  textAlign: 'right',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}))

// Compact date: "12 Jan 2024 · 3 months ago"
function CompactDate({ main, sub }) {
  if (!main) return null
  return (
    <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#111827', textAlign: 'right', lineHeight: 1.3 }}>
      {main}
      {sub && (
        <Typography component="span" sx={{ fontSize: '10px', fontWeight: 400, color: '#9ca3af', ml: '4px' }}>
          · {sub}
        </Typography>
      )}
    </Typography>
  )
}

const Badge = styled(Box)(({ theme, variant = 'purple' }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: '20px',
  minWidth: '20px',
  borderRadius: '5px',
  background: variant === 'green'
    ? 'rgba(16, 185, 129, 0.1)'
    : 'rgba(99, 102, 241, 0.1)',
  color: variant === 'green' ? '#059669' : '#6366f1',
  fontWeight: 700,
  fontSize: '12px',
  padding: '0 7px',
}))

const EmptyState = styled(Typography)(() => ({
  fontSize: '11px',
  color: '#9ca3af',
  fontStyle: 'italic',
  padding: '2px 0',
}))

// ─── Component ───────────────────────────────────────────────────────────────

export default function PatientVisitSummaryHandler({
  patientVisitSummary,
  role = 'doctor',
  showTherapyHistory = true,
  showConsultationHistory = true,
  onReferForTherapy,
  onReferForAssessment,
}) {
  useTheme()

  const onReferForTherapyRef = useRef(onReferForTherapy)
  const onReferForAssessmentRef = useRef(onReferForAssessment)

  useEffect(() => {
    onReferForTherapyRef.current = onReferForTherapy
    onReferForAssessmentRef.current = onReferForAssessment
  }, [onReferForTherapy, onReferForAssessment])

  if (!patientVisitSummary) return null

  function formatDateWithRelative(date) {
    if (!date) return null
    const dateObj = dayjs(date)
    const formatted = dateObj.format('DD MMM YYYY')
    const relative = getExactDiffFromCurrentTimeDayJs(dateObj, dayjs(), true)
    return { formatted, relative }
  }

  // ── Therapist role ──────────────────────────────────────────────────────────
  if (role === 'therapist') {
    const therapyData = {
      patientSince: patientVisitSummary.firstVisitDateForCurrentPractitioner,
      totalSessions: patientVisitSummary.sessionsCompletedForCurrentEventTypeForCurrentMentor,
      lastSession: patientVisitSummary.lastVisitDate,
      therapyStarted: patientVisitSummary.inTherapySince,
      therapySessions: patientVisitSummary.therapySessionsConducted,
    }
    const consultationData = { consultingDoctor: patientVisitSummary.referringPractitionerName }

    const patientSinceDate = formatDateWithRelative(therapyData.patientSince)
    const lastSessionDate = formatDateWithRelative(therapyData.lastSession)
    const therapyStartedDate = formatDateWithRelative(therapyData.therapyStarted)

    return (
      <SummaryContainer>
        {showTherapyHistory && (
          <SummaryCard gradient="linear-gradient(90deg, #6366f1 0%, #8b5cf6 100%)">
            <CardHeader>
              <IconWrapper bgColor="rgba(99, 102, 241, 0.12)">
                <Psychology sx={{ fontSize: 16, color: '#6366f1' }} />
              </IconWrapper>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, minWidth: 0 }}>
                <CardTitle>Therapy History</CardTitle>
                {patientSinceDate && (
                  <Typography sx={{ fontSize: '10px', color: '#9ca3af', whiteSpace: 'nowrap' }}>
                    since {patientSinceDate.formatted}
                  </Typography>
                )}
              </Box>
            </CardHeader>
            <InfoGrid>
              {(typeof therapyData.totalSessions === 'number' || (isValidNumber(therapyData.therapySessions) && therapyData.therapySessions > 0)) && (
                <InfoRow>
                  <InfoLabel><Healing sx={{ fontSize: 11, color: '#6366f1' }} />Sessions</InfoLabel>
                  <InfoValue>
                    {typeof therapyData.totalSessions === 'number' && isValidNumber(therapyData.therapySessions) && therapyData.therapySessions > 0 ? (
                      therapyData.totalSessions === therapyData.therapySessions ? (
                        <Badge>with you {therapyData.totalSessions}</Badge>
                      ) : (
                        <Badge>you {therapyData.totalSessions} / total {therapyData.therapySessions}</Badge>
                      )
                    ) : typeof therapyData.totalSessions === 'number' ? (
                      <Badge>with you {therapyData.totalSessions}</Badge>
                    ) : (
                      <Badge>total {therapyData.therapySessions}</Badge>
                    )}
                  </InfoValue>
                </InfoRow>
              )}
              {therapyStartedDate && (
                <InfoRow>
                  <InfoLabel><Event sx={{ fontSize: 11, color: '#6366f1' }} />Started</InfoLabel>
                  <InfoValue><CompactDate main={therapyStartedDate.formatted} sub={therapyStartedDate.relative} /></InfoValue>
                </InfoRow>
              )}
              {lastSessionDate && (
                <InfoRow>
                  <InfoLabel><CalendarToday sx={{ fontSize: 11, color: '#6366f1' }} />Last session</InfoLabel>
                  <InfoValue><CompactDate main={lastSessionDate.formatted} sub={lastSessionDate.relative} /></InfoValue>
                </InfoRow>
              )}
              {!therapyData.patientSince && typeof therapyData.totalSessions !== 'number' && !therapyData.lastSession && !therapyData.therapyStarted && (!isValidNumber(therapyData.therapySessions) || therapyData.therapySessions === 0) && (
                <EmptyState>No therapy history available</EmptyState>
              )}
            </InfoGrid>
          </SummaryCard>
        )}

        {showConsultationHistory && (
          <SummaryCard gradient="linear-gradient(90deg, #10b981 0%, #059669 100%)">
            <CardHeader>
              <IconWrapper bgColor="rgba(16, 185, 129, 0.12)">
                <LocalHospital sx={{ fontSize: 16, color: '#10b981' }} />
              </IconWrapper>
              <CardTitle>Consultation History</CardTitle>
            </CardHeader>
            <InfoGrid>
              {consultationData.consultingDoctor && consultationData.consultingDoctor !== '--' ? (
                <InfoRow>
                  <InfoLabel><LocalHospital sx={{ fontSize: 11, color: '#10b981' }} />Referring doctor</InfoLabel>
                  <InfoValue><ValueText>{consultationData.consultingDoctor}</ValueText></InfoValue>
                </InfoRow>
              ) : (
                <EmptyState>No consultation history available</EmptyState>
              )}
            </InfoGrid>
          </SummaryCard>
        )}
      </SummaryContainer>
    )
  }

  // ── Doctor role ─────────────────────────────────────────────────────────────
  const patientSinceDate = formatDateWithRelative(patientVisitSummary.patientSince)
  const lastVisitDate = formatDateWithRelative(patientVisitSummary.lastVisitDate)
  const therapyStartedDate = formatDateWithRelative(patientVisitSummary.inTherapySince)
  const lastTherapyVisitDate = formatDateWithRelative(patientVisitSummary.lastTherapyVisitSince)
  const latestTherapyVisitDate = formatDateWithRelative(patientVisitSummary.latestTherapyVisitTime)

  const therapyEmpty =
    !patientVisitSummary.referringPractitionerName &&
    (!isValidNumber(patientVisitSummary.therapySessionsConducted) || patientVisitSummary.therapySessionsConducted === 0) &&
    !therapyStartedDate && !lastTherapyVisitDate && !latestTherapyVisitDate

  return (
    <Box sx={{ width: '100%' }}>
      <SummaryContainer>
        {/* Consultation Summary */}
        <SummaryCard gradient="linear-gradient(90deg, #10b981 0%, #059669 100%)">
          <CardHeader>
            <IconWrapper bgColor="rgba(16, 185, 129, 0.12)">
              <LocalHospital sx={{ fontSize: 16, color: '#10b981' }} />
            </IconWrapper>
            <CardTitle>Consultation Summary</CardTitle>
          </CardHeader>
          <InfoGrid>
            {typeof patientVisitSummary.sessionsCompletedForCurrentEventTypeForCurrentMentor === 'number' && (
              <InfoRow>
                <InfoLabel><Healing sx={{ fontSize: 11, color: '#10b981' }} />Total consultations</InfoLabel>
                <InfoValue>
                  <Badge variant="green">
                    {patientVisitSummary.sessionsCompletedForCurrentEventTypeForCurrentMentor}
                  </Badge>
                </InfoValue>
              </InfoRow>
            )}
            {patientSinceDate && (
              <InfoRow>
                <InfoLabel><Person sx={{ fontSize: 11, color: '#10b981' }} />Patient since</InfoLabel>
                <InfoValue><CompactDate main={patientSinceDate.formatted} sub={patientSinceDate.relative} /></InfoValue>
              </InfoRow>
            )}
            {lastVisitDate && (
              <InfoRow>
                <InfoLabel><CalendarToday sx={{ fontSize: 11, color: '#10b981' }} />Last consultation</InfoLabel>
                <InfoValue><CompactDate main={lastVisitDate.formatted} sub={lastVisitDate.relative} /></InfoValue>
              </InfoRow>
            )}
          </InfoGrid>
        </SummaryCard>

        {/* Therapy Summary */}
        <SummaryCard gradient="linear-gradient(90deg, #6366f1 0%, #8b5cf6 100%)">
          <CardHeader>
            <IconWrapper bgColor="rgba(99, 102, 241, 0.12)">
              <Psychology sx={{ fontSize: 16, color: '#6366f1' }} />
            </IconWrapper>
            <CardTitle>Therapy Summary</CardTitle>
          </CardHeader>

          {therapyEmpty ? (
            onReferForTherapy || onReferForAssessment ? (
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', pt: 0.5 }}>
                {onReferForTherapy && (
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<Psychology sx={{ fontSize: 14 }} />}
                    onClick={(e) => {
                      e.preventDefault(); e.stopPropagation()
                      ;(onReferForTherapyRef.current || onReferForTherapy)?.()
                    }}
                    sx={{
                      borderColor: '#6366f1',
                      color: '#6366f1',
                      fontSize: '12px',
                      fontWeight: 600,
                      padding: '3px 10px',
                      textTransform: 'none',
                      '&:hover': { background: 'rgba(99,102,241,0.06)', borderColor: '#4f46e5' },
                    }}
                  >
                    Refer for Therapy
                  </Button>
                )}
                {onReferForAssessment && (
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<Assignment sx={{ fontSize: 14 }} />}
                    onClick={(e) => {
                      e.preventDefault(); e.stopPropagation()
                      ;(onReferForAssessmentRef.current || onReferForAssessment)?.()
                    }}
                    sx={{
                      borderColor: '#6366f1',
                      color: '#6366f1',
                      fontSize: '12px',
                      fontWeight: 600,
                      padding: '3px 10px',
                      textTransform: 'none',
                      '&:hover': { background: 'rgba(99,102,241,0.06)', borderColor: '#4f46e5' },
                    }}
                  >
                    Refer for Assessment
                  </Button>
                )}
              </Box>
            ) : (
              <InfoGrid><EmptyState>No therapy history</EmptyState></InfoGrid>
            )
          ) : (
            <InfoGrid>
              {patientVisitSummary.referringPractitionerName && (
                <InfoRow>
                  <InfoLabel><Person sx={{ fontSize: 11, color: '#6366f1' }} />Therapist</InfoLabel>
                  <InfoValue><ValueText>{patientVisitSummary.referringPractitionerName}</ValueText></InfoValue>
                </InfoRow>
              )}
              {isValidNumber(patientVisitSummary.therapySessionsConducted) && patientVisitSummary.therapySessionsConducted > 0 && (
                <InfoRow>
                  <InfoLabel><Healing sx={{ fontSize: 11, color: '#6366f1' }} />Therapy sessions</InfoLabel>
                  <InfoValue><Badge>{patientVisitSummary.therapySessionsConducted}</Badge></InfoValue>
                </InfoRow>
              )}
              {therapyStartedDate && (
                <InfoRow>
                  <InfoLabel><Event sx={{ fontSize: 11, color: '#6366f1' }} />Therapy started</InfoLabel>
                  <InfoValue><CompactDate main={therapyStartedDate.formatted} sub={therapyStartedDate.relative} /></InfoValue>
                </InfoRow>
              )}
              {lastTherapyVisitDate && (
                <InfoRow>
                  <InfoLabel><CalendarToday sx={{ fontSize: 11, color: '#6366f1' }} />Last therapy visit</InfoLabel>
                  <InfoValue><CompactDate main={lastTherapyVisitDate.formatted} sub={lastTherapyVisitDate.relative} /></InfoValue>
                </InfoRow>
              )}
              {latestTherapyVisitDate && !lastTherapyVisitDate && (
                <InfoRow>
                  <InfoLabel><CalendarToday sx={{ fontSize: 11, color: '#6366f1' }} />Last therapy session</InfoLabel>
                  <InfoValue><CompactDate main={latestTherapyVisitDate.formatted} sub={latestTherapyVisitDate.relative} /></InfoValue>
                </InfoRow>
              )}
            </InfoGrid>
          )}
        </SummaryCard>
      </SummaryContainer>
    </Box>
  )
}
